import { clsx } from 'clsx'
import { useEffect, useId, useSyncExternalStore, type CSSProperties, type ReactNode } from 'react'

export interface SkeletonProps {
  variant: 'line' | 'block' | 'band' | 'tile'
  width?: string | number
  /** For `line`: how many bars (widths 90%, 70%, 40% repeating). */
  lines?: number
  className?: string
}

/**
 * Loading state: grey bars, never a spinner and never a placeholder number (Yousef's guideline, DECISIONS 13).
 * Static: no shimmer and no loop (r5 bans loops; this is a deliberate departure from the prototype's shimmer).
 * Wrap a group in `<SkeletonGroup label="Loading ...">` so the busy state is announced once.
 */
export function Skeleton({ variant, width, lines = 1, className }: SkeletonProps) {
  const style: CSSProperties | undefined = width === undefined ? undefined : { width }
  if (variant === 'line' && lines > 1) {
    return (
      <span className={clsx('r5-skeleton-lines', className)} aria-hidden="true">
        {Array.from({ length: lines }, (_, index) => (
          <span key={index} className="r5-skeleton r5-skeleton--line" style={{ width: width ?? ['90%', '70%', '40%'][index % 3] }} />
        ))}
      </span>
    )
  }
  return <span className={clsx('r5-skeleton', `r5-skeleton--${variant}`, className)} style={style} aria-hidden="true" />
}

/* Several skeleton groups often mount at once (Home has six). Only the first one in mount order announces "Loading": the others
   keep `aria-busy` but stay silent, so a screen reader hears one status, not a stack of them. When the leader goes, the next takes over. */
const order: string[] = []
const listeners = new Set<() => void>()
const emit = () => listeners.forEach((listener) => listener())
const subscribe = (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener) } }
const leader = () => order[0] ?? null

/** A busy region: `aria-busy` plus one visually hidden status message for assistive technology (from the first group only). */
export function SkeletonGroup({ label, children, className }: { label: string; children: ReactNode; className?: string }) {
  const id = useId()
  const current = useSyncExternalStore(subscribe, leader, () => null)
  useEffect(() => {
    order.push(id)
    emit()
    return () => {
      order.splice(order.indexOf(id), 1)
      emit()
    }
  }, [id])
  return (
    <div className={className} aria-busy="true">
      {current === id ? <span className="r5-sr" role="status">{label}</span> : <span className="r5-sr" aria-hidden="true">{label}</span>}
      {children}
    </div>
  )
}
